import { PackageCheck, ClipboardList, PhoneIncoming } from 'lucide-react';
import { AREAS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore, useAuthStore } from 'store';
import { CardTitle } from 'components/ui/card';
import { cn } from 'utils/classnames';
import { ScreenType } from '../types';
import { isGradingAllowedToday } from 'services/api/product';
import { hasAreaAccess } from 'utils/permissions';

const apps = [
  {
    id: 'receiving',
    label: 'Receiving',
    icon: PackageCheck,
    screen: SCREEN_TYPES.RECEIVING,
    color: 'bg-blue-600',
    area: AREAS.ARRIVAL,
  },
  {
    id: 'grading',
    label: 'Grading',
    icon: ClipboardList,
    screen: SCREEN_TYPES.SCAN_BOX,
    color: 'bg-emerald-600',
    area: AREAS.GRADING,
  },
  {
    id: 'callback',
    label: 'Callback',
    icon: PhoneIncoming,
    screen: SCREEN_TYPES.CALLBACK,
    color: 'bg-rose-600',
    area: AREAS.PALLET_BUILD,
  },
] as const;

export default function AppSelect() {
  const { setCurrentScreen } = useAppStore();
  const { user } = useAuthStore();
  const visibleApps = apps.filter((app) =>
    hasAreaAccess(user?.roles ?? [], app.area, user?.isAdmin)
  );

  const onScreenChange = async (screen: ScreenType) => {
    let currentScreen = screen;
    if (screen === SCREEN_TYPES.SCAN_BOX) {
      const isGradingAllowed = await isGradingAllowedToday();
      if (!isGradingAllowed) {
        currentScreen = SCREEN_TYPES.GRADING_DISABLED;
      }
    }
    setCurrentScreen(currentScreen);
  };

  return (
    <div className="space-y-6">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Select App
      </CardTitle>

      <div className="grid grid-cols-2 gap-4 px-2 py-4">
        {visibleApps.map(({ id, label, icon: Icon, screen, color }) => (
          <button
            key={id}
            onClick={() => onScreenChange(screen)}
            className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all active:scale-95 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <div
              className={cn(
                'flex h-16 w-16 items-center justify-center rounded-2xl  text-white shadow-md',
                color
              )}
            >
              <Icon className="h-8 w-8" />
            </div>
            <span className="text-sm font-semibold text-slate-800">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
